import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { Module } from "@/constants";
import { PROJECT_DEPENDENCY_DEPARTMENT } from "@/schemas/commonFields";
import { laborCostsInStoresInitialDepartmentBased } from "@/services/laborCostsInStoresInitialDepartmentBased";
import { laborCostsInStoresInitialRoleBased } from "@/services/laborCostsInStoresInitialRoleBased";
import { FetchListRequest, SuccessFetchListResponse } from "@/services/api";
import { ReadLaborCostsInStoresInitial } from "@/types/laborCostsInStoresInitial";
import { mapInitialStores } from "../utils/mapInitialStores";

interface InitialLaborCostsService {
  getList: (
    settlementId: string,
    fetchParams?: FetchListRequest<ReadLaborCostsInStoresInitial>,
  ) => Promise<SuccessFetchListResponse<ReadLaborCostsInStoresInitial>>;
}

interface UseInitialStoresParams {
  projectId?: string;
  settlementId: string;
  projectDependsOn?: string;
}

export const useInitialStores = ({
  projectId,
  settlementId,
  projectDependsOn,
}: UseInitialStoresParams) => {
  const isProjectDependsOnDepartment =
    projectDependsOn === PROJECT_DEPENDENCY_DEPARTMENT;

  const service: InitialLaborCostsService = useMemo(
    () =>
      isProjectDependsOnDepartment
        ? laborCostsInStoresInitialDepartmentBased
        : laborCostsInStoresInitialRoleBased,
    [isProjectDependsOnDepartment],
  );

  const storesQuery = useQuery<
    SuccessFetchListResponse<ReadLaborCostsInStoresInitial>
  >({
    queryKey: [
      Module.Projects,
      projectId,
      "invoice-specifications",
      "init-stores",
    ],
    queryFn: () => service.getList(settlementId),
    enabled: Boolean(projectDependsOn),
  });

  const stores = useMemo(() => {
    if (!storesQuery.data?.items) {
      return [];
    }
    return mapInitialStores(
      storesQuery.data.items,
      isProjectDependsOnDepartment,
    );
  }, [storesQuery.data, isProjectDependsOnDepartment]);

  return {
    stores,
    isPending: storesQuery.isPending,
  };
};
